import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import TimesheetService from "../TimesheetService";
import {
    DKLabel,
    DKButton,
    DKInput,
    DKCheckMark,
    DKTooltipWrapper,
    DKIcon,
    DKIcons,
    DKSpinner,
    Toggle,
    INPUT_TYPE,
    INPUT_VIEW_DIRECTION,
    showLoader,
    removeLoader,
    showToast,
    TOAST_TYPE
} from "deskera-ui-library";
import { useSelector } from "react-redux";
import { selectProject } from "../../../Redux/Slices/ProjectSlice";
import TimesheetUtils from "../TimesheetUtils";

function AddTask(props) {
    const { t } = useTranslation();
    const project = useSelector(selectProject);
    const initialFormState = {
        name: {
            id: "name",
            title: t("Name"),
            type: INPUT_TYPE.TEXT,
            validation: [["required"]]
        },
        description: {
            id: "description",
            title: t("Description"),
            type: INPUT_TYPE.TEXT
        },
        projectId: {
            id: "projectId",
            title: t("Project"),
            type: INPUT_TYPE.DROPDOWN,
            options: project?.data?.map(e=>{ return {...e,label:e.name,value: e.id}})
        },
        isBillable: {
            id: "isBillable",
            title: t("Billable"),
            type: "toggle",
            value: false
        }
    }

    const [formState, setFormState] = useState(initialFormState);
    const [task, setTask] = useState(null);
    const [fetchingDependencies, setFetchingDependencies] = useState(true);
 
    useEffect(() => {
        if (TimesheetService.TASK) {
            setTask(TimesheetService.TASK);
            TimesheetService.TASK = null;
        }

        fetchDependencies();
    }, []);

    useEffect(() => {
        if (!fetchingDependencies) {
            const newState = { ...formState };

            if (task) {
                setTaskData(newState);
            }

            setFormState(newState);
        }
    }, [fetchingDependencies, task]);

    const fetchDependencies = async () => {
        setFetchingDependencies(false);
    }

    const setTaskData = (newState) => {
        newState.name.value = task.name?.trim();
        newState.description.value = task.description;
        newState.projectId.value = getDropdownLabel(task.projectId, newState.projectId.options);
        newState.isBillable.value = task.isBillable;
    }

    const getDropdownLabel = (v, o) => {
        let value = v;

        const index = o.findIndex(i => i.value === v);
        if (index !== -1) {
            value = o[index].label;
        }

        return value;
    }

    const getDropdownValue = (v, o) => {
        let value = v;

        const index = o.findIndex(i => i.label === v);
        if (index !== -1) {
            value = o[index].value;
        }

        return value;
    }

    const getDropdownObj = (v, o) => {
        let value = v;

        const index = o.findIndex(i => i.label === v);
        if (index !== -1) {
            value = o[index];
        }

        return value;
    }

    const isRequired = (validation) => {
        return validation ? validation.findIndex((v) => v[0] === "required") !== -1 : false;
    }

    const hasError = (field, value) => {
        let error = {
            invalid: false,
            errorMessage: null
        };

        const validation = field.validation;

        if (validation && validation.length !== 0) {
            validation.forEach((validation) => {
                switch (validation[0]) {
                    case "required":
                        if (value === null || value === undefined || String(value).trim() === "") {
                            error.invalid = true;
                        }
                        break;
                    default:
                        error.invalid = false;
                        error.errorMessage = null;
                        break;
                }
            });
        }

        return error;
    }

    const updateState = (stateName, field, value) => {
        const newState = { ...formState };
        newState[stateName] = { ...field, value: value, ...hasError(field, value) };
        updateValueAndValidity(stateName, newState);

        setFormState(newState);
    }

    const updateValueAndValidity = (stateName, state) => {
        switch (stateName) {
            case "projectId":
                const project = getDropdownObj(state.projectId.value, state.projectId.options);
                if (project) {
                    state.isBillable.value = project.isBillable;
                }
                break;
            }
    }

    const isInvalid = () => {
        let invalid = false;

        const newState = { ...formState };
        Object.entries(newState).forEach(([key, field]) => {
            const error = hasError(field, field.value);
            field.invalid = error.invalid;
            field.errorMessage = error.errorMessage;

            if (field.invalid) {
                invalid = true;
            }
        });

        setFormState(newState);

        return invalid;
    }

    const getPayload = () => {
        const payload = {
            name: formState.name.value,
            description: formState.description.value,
            projectId: getDropdownValue(formState.projectId.value, formState.projectId.options),
            isBillable: formState.isBillable.value
        };

        if (task) {
            payload.id = task.id;
        }

        return payload;
    }

    const saveTask = () => {
        if (isInvalid()) {
            return;
        }

        showLoader();
        const payload = getPayload();
        if (task) {
            TimesheetService.updateTask(payload).then(res=>{
                removeLoader();
                showToast("Task updated successfully.", TOAST_TYPE.SUCCESS);
                props.onClose(true);
            },error=>{
                removeLoader();
                const message = error && error.message ? error.message : t("SOMETHING_WENT_WRONG")
                showToast(message, TOAST_TYPE.FAILURE);
            })
        } else {
            TimesheetService.saveTask(payload).then(res=>{
                removeLoader();
                showToast("Task added successfully.", TOAST_TYPE.SUCCESS);
                props.onClose(true);
            },error=>{
                removeLoader();
                const message = error && error.message ? error.message : t("SOMETHING_WENT_WRONG")
                showToast(message, TOAST_TYPE.FAILURE);
            })
        }
        
        
    }

    const renderField = (stateName, field, width, className) => {
        switch (field.type) {
            case INPUT_TYPE.TEXT:
            case INPUT_TYPE.NUMBER:
            case INPUT_TYPE.DATE:
                return (
                    <div key={field.id} style={{ flex: `${width}`, maxWidth: width }}>
                        <div className={`row position-relative ${!className ? "pb-l mb-s" : className}`}>
                            <DKInput
                                ref={field.ref}
                                title={field.title}
                                type={field.type}
                                direction={INPUT_VIEW_DIRECTION.HORIZONTAL}
                                value={field.value}
                                errorMessage={field.errorMessage}
                                readOnly={!!field.readOnly || fetchingDependencies}
                                required={isRequired(field.validation)}
                                tooltip={{
                                    content: field.tooltip,
                                    className: "popup-tooltip-prop"
                                }}
                                invalid={field.invalid}
                                canValidate={field.invalid}
                                onChange={(value) => {
                                    updateState(stateName, field, value);
                                }}
                                validator={field.validator}
                            />
                        </div>
                    </div>
                );
            case INPUT_TYPE.DROPDOWN:
                return (
                    <div key={field.id} style={{ flex: `${width}`, maxWidth: width }}>
                        <div className={`row position-relative ${!className ? "pb-l mb-s" : className}`}>
                            <DKInput
                                title={field.title}
                                type={INPUT_TYPE.DROPDOWN}
                                direction={INPUT_VIEW_DIRECTION.HORIZONTAL}
                                dropdownConfig={{
                                    allowSearch: field.options?.length > 6,
                                    searchableKey: "label",
                                    data: field.options,
                                    renderer: (i, item) => item.label,
                                    onSelect: (i, value) => {
                                        updateState(stateName, field, value.label);
                                    }
                                }}
                                value={field.value}
                                errorMessage={field.errorMessage}
                                readOnly={!!field.readOnly || fetchingDependencies}
                                required={isRequired(field.validation)}
                                invalid={field.invalid}
                                canValidate={field.invalid}
                                tooltip={{
                                    content: field.tooltip,
                                    className: "popup-tooltip-prop"
                                }}
                                onChange={() => { }}
                            />
                        </div>
                    </div>
                );
            case INPUT_TYPE.MULTI_SELECT:
                return (
                    <div key={field.id} style={{ flex: `${width}`, maxWidth: width }}>
                        <div className={`row position-relative ${!className ? "pb-l mb-s" : className} ${field.readOnly && "opacity-5"}`}>
                            <DKInput
                                title={field.title}
                                type={INPUT_TYPE.DROPDOWN}
                                direction={INPUT_VIEW_DIRECTION.HORIZONTAL}
                                dropdownConfig={{
                                    multiSelect: true,
                                    checkMarkColor: "bg-app",
                                    allowSearch: field.options?.length > 6,
                                    searchableKey: "label",
                                    data: field.options,
                                    renderer: (i, item) => item.label,
                                    onSelect: (i, value) => { },
                                }}
                                displayKey={"label"}
                                value={field.value}
                                errorMessage={field.errorMessage}
                                readOnly={!!field.readOnly || fetchingDependencies}
                                required={isRequired(field.validation)}
                                invalid={field.invalid}
                                canValidate={field.invalid}
                                tooltip={{
                                    content: field.tooltip,
                                    className: "popup-tooltip-prop"
                                }}
                                onChange={(value) => {
                                    updateState(stateName, field, value);
                                }}
                            />
                        </div>
                    </div>
                );
            case INPUT_TYPE.CHECKBOX:
                return (
                    <div key={field.id} style={{ flex: `${width}`, maxWidth: width }}>
                        <div className="row position-relative pb-l mb-s">
                            <div className="d-flex dkinput-header-section mr-l">
                                <DKLabel className="mb-xs" text={`${field.title} ${isRequired(field.validation) ? "*" : ""}`} />
                                {field.tooltip &&
                                    <DKTooltipWrapper content={field.tooltip} tooltipClassName="checkbox-tooltip-prop">
                                        <DKIcon src={DKIcons.ic_info} className="ic-s ml-s cursor-hand" style={{ opacity: 0.4 }} />
                                    </DKTooltipWrapper>
                                }
                            </div>
                            <DKCheckMark
                                isSelected={field.value}
                                color="bg-app"
                                onClick={() => {
                                    updateState(stateName, field, !field.value);
                                }}
                            />
                        </div>
                    </div>
                );
            case "toggle":
                return (
                    <div key={field.id} style={{ flex: "0 0 100%", maxWidth: width }}>
                        <div className="row pb-l mb-s position-relative">
                            <DKLabel style={{ width: "46%" }} className="mb-xs" text={isRequired(field.validation) ? `${field.title} *` : field.title} />
                            <Toggle
                                style={{ width: "20%" }}
                                isOn={field.value}
                                onChange={() => {
                                    updateState(stateName, field, !field.value);
                                }}
                            />
                        </div>
                    </div>
                );
            default:
                break;
        }
    }

    return (
        <div className="transparent-background">
            <div className="popup-window" style={{ padding: 0, overflow: "hidden" }}>
                <div className="parent-width" style={{ height: "50vh" }}>
                    <div className="row p-h-xl pt-xl pb-m">
                        <DKLabel text={task ? t("Edit Task") : t("Add Task")} className="fs-l fw-b" />
                        {fetchingDependencies && <DKSpinner className="pl-s" iconClassName="ic-s-2" />}
                        <div className="ml-auto d-flex">
                            <DKButton
                                title={t("CANCEL")}
                                className="border-radius-m bg-gray1 border-m"
                                onClick={() => {
                                    props.onClose();
                                }}
                            />
                            <DKButton
                                title={t("SAVE")}
                                className="bg-app text-white ml-m"
                                onClick={() => {
                                    saveTask();
                                }}
                            />
                        </div>
                    </div>
                    <div className="parent-width custom-scroll" style={{ overflow: "auto", height: "calc(100% - 69px)" }}>
                        <div className="p-h-xl p-v-m">
                            {renderField("name", formState.name, "100%")}
                            {renderField("description", formState.description, "100%")}
                            {renderField("projectId", formState.projectId, "100%")}
                            {renderField("isBillable", formState.isBillable, "100%")}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AddTask;
